import React from 'react'
import type { NextPage, GetServerSidePropsContext } from 'next'
import { useTranslations } from 'next-intl'
import { Box, useColorModeValue } from '@chakra-ui/react'
import { InfoFrame } from '@/components/InfoFrame'
import { PageHero } from '@/components/PageHero'
import { Highlights } from '@/components/Highlights'
import { ContainerBase } from '@/components/containers/ContainerBase'
import { ShopNowCard } from '@/components/ShopNowCard'
import { AutoRenewIcon } from '@/components/icons/AutoRenewIcon'
import { PayByWireIcon } from '@/components/icons/PayByWireIcon'
import { AddCheck } from '@/components/icons/AddCheck'
import { VerticalSlideshow } from '@/components/VerticalSlideshow'
import { HighlightItem } from '@/types/highlights.interface'
import { ISlide } from '@/types/strapi/commonComponents.interface'
import SeoHead from '@/components/seo-head'

const PrepaidCreditsPage: NextPage = () => {
  const t = useTranslations()
  const bg = useColorModeValue('blackAlpha.50', 'whiteAlpha.100')

  const items: HighlightItem[] = [
    {
      icon: PayByWireIcon,
      title: t('autopay.choose-payment'),
      description: t('autopay.preferred-method'),
    },
    {
      icon: AutoRenewIcon,
      title: t('autopay.enable-automatic-payments'),
      description: t('autopay.turn-automatic-payments'),
    },
    {
      icon: AddCheck,
      title: t('autopay.stay-current'),
      description: t('autopay.paid-automatically'),
    },
  ]

  const billingSlideshow: ISlide[] = [
    {
      id: t('autopay.hands-free'),
      title: t('autopay.hands-free'),
      description: t('autopay.goodbye-tedious-payments'),
      image: { url: '/img/invoices-page.jpg' },
      imageMobile: { url: '/img/invoices-page.jpg' },
    },
    {
      id: t('autopay.never-miss-payment'),
      title: t('autopay.never-miss-payment'),
      description: t('autopay.robust-auto-pay'),
      image: { url: '/img/autopay-page-mobile.jpg' },
      imageMobile: { url: '/img/autopay-page-mobile.jpg' },
    },
    {
      id: t('autopay.avoid-late-fees'),
      title: t('autopay.avoid-late-fees'),
      description: t('autopay.overlooked-risk'),
      image: { url: '/img/invoice-detail-page.jpg' },
      imageMobile: { url: '/img/invoice-detail-page.jpg' },
    },
    {
      id: t('autopay.seamless-credit-usage'),
      title: t('autopay.seamless-credit-usage'),
      description: t('autopay.cover-hosting-invoices'),
      image: { url: '/img/auto-apply-credits.jpg' },
      imageMobile: { url: '/img/auto-apply-credits.jpg' },
    },
  ]

  return (
    <>
      <SeoHead
        title={t('autopay.seo.title')}
        breadcrumbTitle={t('autopay.seo.breadcrumb')}
        description={t('autopay.seo.description')}
      />
      <PageHero
        title={t('autopay.simplify-process')}
        subTitle={t('dashboard.billing.automatic-payments')}
        description={t('autopay.recurring-billing')}
        image="img/autopay-hero.png"
        mobileImage="img/autopay-hero.png"
        anchor="what-are-automatic-payments"
      />
      <Box id="what-are-automatic-payments">
        <InfoFrame
          title={t('autopay.what-are-automatic-payments')}
          description={t.rich('autopay.ultimate-convenience', {
            br: () => <br />,
          })}
        />
      </Box>
      <Highlights
        title={t('autopay.how-to-automate')}
        items={items}
        columns={3}
      />
      <ShopNowCard />
      <Box bg={bg}>
        <ContainerBase px={{ base: '32px', md: 4 }}>
          <Box py={{ base: '64px', md: '100px' }}>
            <VerticalSlideshow
              title={t('autopay.effortless-billing')}
              subtitle={t('autopay.enjoy-peace')}
              slideshow={billingSlideshow}
              alignStart
            />
          </Box>
        </ContainerBase>
      </Box>
    </>
  )
}

export async function getStaticProps({ locale }: GetServerSidePropsContext) {
  return {
    props: {
      messages: (await import(`../../../locales/${locale}.json`)).default,
    },
  }
}

export default PrepaidCreditsPage
